import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import usePermissions from '~hooks/usePermissions';
import {
  addItemToPrimitiveArray,
  moveItemInsideArray,
  removeItemFromArrayAtPosition,
  updateItemInArrayAtPosition,
} from '~services/immutable';
import { fetchChangelogItems } from '../api/calls/fetchChangelogItems';
import { patchChangelogItem } from '../api/calls/patchChangelogItem';
import { removeChangelogItem } from '../api/calls/removeChangelogItem';
import { ChangelogContext } from './ChangelogContext';

type ChangelogProviderProps = {
  children: ReactNode;
};

export function ChangelogProvider({ children }: ChangelogProviderProps) {
  const { hasManageChangelogPermission } = usePermissions();

  const [items, setItems] = useState<ChangelogItem[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [fetchIndex, setFetchIndex] = useState(0);
  const [fetchTotalCount, setFetchTotalCount] = useState(0);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    async function initialFetch() {
      setIsFetching(true);
      const response = await fetchChangelogItems();
      setItems(response.items);
      setFetchIndex(response.items.length);
      setFetchTotalCount(response.totalCount);
      setIsFetching(false);
      setHasLoaded(true);
    }
    void initialFetch();
    // eslint-disable-next-line
  }, []);

  const fetchMoreChangelogItems = useCallback(() => {
    async function fetchMore(startIndex: number) {
      setIsFetching(true);
      const response = await fetchChangelogItems(startIndex);
      setItems(oldItems => [...oldItems, ...response.items]);
      setFetchIndex(startIndex + response.items.length);
      setFetchTotalCount(response.totalCount);
      setIsFetching(false);
    }
    if (isFetching) {
      return;
    }
    if (fetchIndex >= fetchTotalCount) {
      return;
    }
    void fetchMore(fetchIndex);
  }, [fetchIndex, fetchTotalCount, isFetching]);

  const updateChangelogItem = useCallback(
    (id: ChangelogItem['id'], description: ChangelogItem['description']) => {
      async function update(id: ChangelogItem['id'], description: ChangelogItem['description']) {
        const indexItemToUpdate = items.findIndex(item => item.id === id);
        if (indexItemToUpdate !== -1) {
          const itemToUpdate = items[indexItemToUpdate];
          // optimistic ui update
          setItems(items =>
            updateItemInArrayAtPosition(
              items,
              { description, originalDescription: itemToUpdate.description },
              indexItemToUpdate,
            ),
          );
          // api call
          const updateSuccess = await patchChangelogItem(id, description);
          if (!updateSuccess) {
            // revert changes
            setItems(items =>
              updateItemInArrayAtPosition(
                items,
                { description: itemToUpdate.originalDescription, originalDescription: undefined },
                indexItemToUpdate,
              ),
            );
          }
        }
      }
      if (description !== null) {
        void update(id, description);
      }
    },
    [items],
  );

  const deleteChangelogItem = useCallback(
    (id: ChangelogItem['id']) => {
      async function remove(id: ChangelogItem['id']) {
        const indexItemToDelete = items.findIndex(item => item.id === id);
        if (indexItemToDelete !== -1) {
          const itemToDelete = items[indexItemToDelete];
          // optimistic ui update
          setItems(items => removeItemFromArrayAtPosition(items, indexItemToDelete));
          setFetchTotalCount(count => count - 1);
          // api call
          const removeSuccess = await removeChangelogItem(id);
          if (!removeSuccess) {
            // revert remove
            setItems(items => moveItemInsideArray(addItemToPrimitiveArray(items, itemToDelete), -1, indexItemToDelete));
            setFetchTotalCount(count => count + 1);
          }
        }
      }
      void remove(id);
    },
    [items],
  );

  const value = useMemo(
    () => ({
      canEdit: hasManageChangelogPermission,
      changelogItems: items,
      deleteChangelogItem,
      fetchMoreChangelogItems,
      hasLoaded,
      hasMore: fetchTotalCount > items.length,
      updateChangelogItem,
    }),
    [
      hasManageChangelogPermission,
      items,
      deleteChangelogItem,
      fetchMoreChangelogItems,
      hasLoaded,
      fetchTotalCount,
      updateChangelogItem,
    ],
  );
  return <ChangelogContext.Provider value={value}>{children}</ChangelogContext.Provider>;
}
